import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Search, Plane, Sparkles, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MobileBottomNav — MakeMyTrip-style fixed bottom tab bar for the
 * traveller experience on mobile devices only. Hidden on partner /
 * admin routes and on md+ breakpoints.
 */
const TABS = [
  { to: "/",             label: "Home",    icon: Home,     testId: "bnav-home"    },
  { to: "/search",       label: "Search",  icon: Search,   testId: "bnav-search"  },
  { to: "/trips",        label: "Trips",   icon: Plane,    testId: "bnav-trips"   },
  { to: "/requests",     label: "Bids",    icon: Sparkles, testId: "bnav-bids"    },
  { to: "/customer",     label: "Profile", icon: User,     testId: "bnav-profile" },
];

export default function MobileBottomNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Only for traveller / logged-out. Hide for partner + admin dashboards.
  const isPartnerPath = pathname.startsWith("/partner") || pathname.startsWith("/admin");
  const isPartner = user && (user.role === "hotel_partner" || user.role === "admin" || user.role === "hotel_partner_pending");
  const hidden = isPartnerPath || isPartner;

  // Toggle body class so pages get bottom padding when bar is visible
  useEffect(() => {
    if (hidden) {
      document.body.classList.remove("has-bottom-nav");
    } else {
      document.body.classList.add("has-bottom-nav");
    }
    return () => document.body.classList.remove("has-bottom-nav");
  }, [hidden]);

  if (hidden) return null;

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#0F1115]/95 backdrop-blur-md"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <ul className="flex items-stretch justify-around h-16">
        {TABS.map(({ to, label, icon: Icon, testId }) => {
          const active =
            to === "/" ? pathname === "/" :
            pathname === to || pathname.startsWith(to + "/");
          return (
            <li key={to} className="flex-1 relative">
              <NavLink
                to={to}
                data-testid={testId}
                className={`flex flex-col items-center justify-center gap-1 h-full text-[10px] font-semibold tracking-wide transition-colors ${
                  active ? "text-[#E8D8A8]" : "text-white/60 hover:text-white"
                }`}
              >
                <Icon
                  className={`w-5 h-5 transition-transform ${active ? "scale-110" : ""}`}
                  strokeWidth={active ? 2.5 : 2}
                />
                <span className="uppercase tracking-[0.14em]">{label}</span>
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-[#E8D8A8] to-[#C8A34D] rounded-full" />
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
